import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { T, tr, LANGS } from "@/i18n/translations";
import { getConfig } from "@/lib/api";

const AppContext = createContext(null);

const SUPPORTED = LANGS.map((l) => l.code);

function detectLang() {
  const urlLang = new URLSearchParams(window.location.search).get("lang");
  if (urlLang && SUPPORTED.includes(urlLang)) return urlLang;
  const stored = localStorage.getItem("ubp_lang");
  if (stored && SUPPORTED.includes(stored)) return stored;
  const nav = (navigator.languages || [navigator.language || "it"]).map((l) => l.slice(0, 2).toLowerCase());
  const match = nav.find((code) => SUPPORTED.includes(code));
  return match || "it";
}

export function AppProvider({ children }) {
  const [lang, setLangState] = useState(detectLang);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    getConfig().then(setConfig).catch(() => {});
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((l) => {
    setLangState(l);
    localStorage.setItem("ubp_lang", l);
  }, []);

  // t("nav.search") -> localized string
  const t = useCallback(
    (path) => {
      const node = path.split(".").reduce((acc, k) => (acc ? acc[k] : undefined), T);
      return tr(node, lang);
    },
    [lang]
  );

  return (
    <AppContext.Provider value={{ lang, setLang, t, config }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}
